import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { ArrowRight, Monitor, Heart, Brain, Baby, Eye, Shield } from 'lucide-react'
import WaterfallFadeIn from './WaterfallFadeIn'
import ultrasoundImg from '../assets/ultrasound.png'
import nuclearMedicineImg from '../assets/nuclearmedicine.png'
import catImg from '../assets/cat.png'

const ServicesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const services = [
    {
      icon: Monitor,
      title: 'Nuclear Medicine',
      description: 'Advanced nuclear medicine imaging systems for precise diagnostic capabilities.',
      image: nuclearMedicineImg,
      features: ['SPECT scanning', 'Radioactive tracers', 'Molecular imaging']
    },
    {
      icon: Brain,
      title: 'CAT Scan',
      description: 'High-resolution computed tomography systems for comprehensive body imaging.',
      image: catImg,
      features: ['Multi-slice CT', '3D reconstruction', 'Low-dose protocols', 'Emergency imaging']
    },
    {
      icon: Heart,
      title: 'Ultrasound',
      description: 'State-of-the-art ultrasound systems for real-time diagnostic imaging.',
      image: ultrasoundImg,
      features: ['Real-time imaging', 'Doppler analysis', '3D/4D capabilities', 'Portable systems']
    },
  ]

  return (
    <section className="section-padding bg-white" id="services">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-primary-500 rounded-full mr-2"></span>
            Our Services
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Comprehensive Medical Imaging Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From advanced ultrasound systems to specialized radiology equipment, 
            we provide cutting-edge technology for every medical imaging need.
          </p>
        </motion.div>

        <WaterfallFadeIn
          baseDelay={0.1}
          duration={0.8}
          direction="up"
          distance={50}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service, index) => (
            <div key={index} className="group">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group-hover:border-primary-200 overflow-hidden">
                {/* Small Image Section */}
                <div className="relative mb-6">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-32 object-cover object-center rounded-lg group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Blue Icon Box - Positioned absolutely in top left */}
                  <div className="absolute top-3 left-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-400 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button className="inline-flex items-center text-primary-500 font-medium hover:text-primary-600 transition-colors duration-200 group-hover:translate-x-1">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          ))}
        </WaterfallFadeIn>
      </div>
    </section>
  )
}

export default ServicesSection 